// Two-panel page for supervisors to link by-product items to their source raw materials
// Left panel: searchable list of raw materials. Right panel: linked by-products for the selected raw material.

import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { Search, Plus, Trash2, X, Link2, Package, ChevronRight } from 'lucide-react';
import byproductMappingService, {
  ByproductMapping,
  InventorySearchResult,
} from '../../services/byproductMappingService';
import { useLanguage } from '../../i18n/LanguageContext';
import { usePageTitle } from '../../contexts/PageTitleContext';
import { useAuth } from '../../hooks/useAuth';

const ByproductMappingPage: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  usePageTitle(t('byproductMapping.title'), t('nav.settings'));
  const queryClient = useQueryClient();

  // State
  const [selectedRawMaterial, setSelectedRawMaterial] = useState<string | null>(null);
  const [rawMaterialSearch, setRawMaterialSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [itemSearch, setItemSearch] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [selectedItem, setSelectedItem] = useState<InventorySearchResult | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  // Queries
  const { data: rawMaterials = [], isLoading: loadingRawMaterials } = useQuery(
    ['byproduct-raw-materials'],
    byproductMappingService.getRawMaterials
  );

  const { data: mappings = [], isLoading: loadingMappings } = useQuery(
    ['byproduct-mappings', selectedRawMaterial],
    () => selectedRawMaterial ? byproductMappingService.getMappings(selectedRawMaterial) : Promise.resolve([]),
    { enabled: !!selectedRawMaterial }
  );

  // Fetch all mappings for the overview panel (always loaded)
  const { data: allMappings = [], isLoading: loadingAllMappings } = useQuery(
    ['byproduct-mappings-all'],
    () => byproductMappingService.getMappings()
  );

  // Derive species letter from selected raw material (first char of item_number)
  const selectedSpecies = selectedRawMaterial ? selectedRawMaterial.charAt(0).toUpperCase() : undefined;

  const { data: searchResults = [], isFetching: searching } = useQuery(
    ['byproduct-search', itemSearch, selectedSpecies],
    () => byproductMappingService.searchItems(itemSearch, selectedSpecies),
    { enabled: itemSearch.length >= 2, keepPreviousData: true }
  );

  // Mutations
  const createMutation = useMutation(byproductMappingService.createMapping, {
    onSuccess: () => {
      toast.success(t('byproductMapping.mappingCreated'));
      queryClient.invalidateQueries(['byproduct-mappings', selectedRawMaterial]);
      queryClient.invalidateQueries(['byproduct-mappings-all']);
      setShowAddModal(false);
      setItemSearch('');
      setNewNotes('');
      setSelectedItem(null);
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.error || 'Failed to create mapping';
      toast.error(msg);
    },
  });

  const deleteMutation = useMutation(byproductMappingService.deleteMapping, {
    onSuccess: () => {
      toast.success(t('byproductMapping.mappingDeleted'));
      queryClient.invalidateQueries(['byproduct-mappings', selectedRawMaterial]);
      queryClient.invalidateQueries(['byproduct-mappings-all']);
      setConfirmDeleteId(null);
    },
    onError: () => {
      toast.error('Failed to delete mapping');
    },
  });

  // Filter raw materials by search
  const filteredRawMaterials = useMemo(() => {
    if (!rawMaterialSearch.trim()) return rawMaterials;
    const q = rawMaterialSearch.toLowerCase();
    return rawMaterials.filter(
      (rm) =>
        rm.item_number.toLowerCase().includes(q) ||
        (rm.item_english_name || '').toLowerCase().includes(q) ||
        (rm.item_chinese_name || '').toLowerCase().includes(q)
    );
  }, [rawMaterials, rawMaterialSearch]);

  // Filter search results to exclude already-linked items
  const filteredSearchResults = useMemo(() => {
    const linkedNumbers = new Set(mappings.map((m) => m.byproduct_item_number));
    return searchResults.filter((item) => !linkedNumbers.has(item.item_number));
  }, [searchResults, mappings]);

  // Group all mappings by raw material for the overview panel
  const groupedMappings = useMemo(() => {
    const groups: Record<string, { rawMaterialName: string | null; rawMaterialChineseName: string | null; byproducts: ByproductMapping[] }> = {};
    for (const m of allMappings) {
      if (!groups[m.raw_material_item_number]) {
        groups[m.raw_material_item_number] = {
          rawMaterialName: m.raw_material_name,
          rawMaterialChineseName: m.raw_material_chinese_name,
          byproducts: [],
        };
      }
      groups[m.raw_material_item_number].byproducts.push(m);
    }
    return Object.entries(groups).sort((a, b) => {
      const nameA = (a[1].rawMaterialName || a[0]).toLowerCase();
      const nameB = (b[1].rawMaterialName || b[0]).toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [allMappings]);

  const handleCreate = () => {
    if (!selectedRawMaterial || !selectedItem) return;
    createMutation.mutate({
      raw_material_item_number: selectedRawMaterial,
      byproduct_item_number: selectedItem.item_number,
      notes: newNotes || undefined,
      created_by: user?.id || user?.username || undefined,
    });
  };

  const selectedRawMaterialInfo = rawMaterials.find(
    (rm) => rm.item_number === selectedRawMaterial
  );

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="mb-4">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('byproductMapping.subtitle')}
        </p>
      </div>

      {/* Two-panel layout */}
      <div className="flex-1 flex flex-col md:flex-row gap-4 min-h-0">
        {/* Left panel: Raw Materials */}
        <div className="w-full md:w-1/3 flex flex-col bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="p-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              {t('byproductMapping.selectRawMaterial')}
            </h3>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={rawMaterialSearch}
                onChange={(e) => setRawMaterialSearch(e.target.value)}
                placeholder={t('byproductMapping.searchRawMaterials')}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loadingRawMaterials ? (
              <div className="p-4 text-center text-sm text-gray-500">Loading...</div>
            ) : filteredRawMaterials.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500">
                {t('byproductMapping.noResults')}
              </div>
            ) : (
              filteredRawMaterials.map((rm) => (
                <button
                  key={rm.item_number}
                  onClick={() => setSelectedRawMaterial(rm.item_number)}
                  className={`w-full text-left px-3 py-2.5 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                    selectedRawMaterial === rm.item_number
                      ? 'bg-primary-50 dark:bg-primary-900/30 border-l-2 border-l-primary-500'
                      : ''
                  }`}
                >
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {rm.item_english_name || rm.item_number}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {rm.item_number}
                    {rm.item_chinese_name && ` · ${rm.item_chinese_name}`}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Right panel: Selected item editing + existing linkages overview */}
        <div className="flex-1 flex flex-col bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex-1 overflow-y-auto">
            {/* Selected raw material editing section */}
            {selectedRawMaterial && (
              <div>
                {/* Selected raw material header with close button */}
                <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between sticky top-0 bg-white dark:bg-gray-800 z-10">
                  <div className="flex items-center gap-2 min-w-0">
                    <button
                      onClick={() => setSelectedRawMaterial(null)}
                      className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded flex-shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </button>
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {t('byproductMapping.linkedByproducts')}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                        {selectedRawMaterialInfo?.item_english_name || selectedRawMaterial}
                        {selectedRawMaterialInfo?.item_chinese_name &&
                          ` (${selectedRawMaterialInfo.item_chinese_name})`}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowAddModal(true);
                      setItemSearch('');
                      setSelectedItem(null);
                      setNewNotes('');
                    }}
                    className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors flex-shrink-0"
                  >
                    <Plus className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('byproductMapping.addByproduct')}</span>
                  </button>
                </div>

                {/* Linked by-products list for selected raw material */}
                <div>
                  {loadingMappings ? (
                    <div className="p-4 text-center text-sm text-gray-500">Loading...</div>
                  ) : mappings.length === 0 ? (
                    <div className="flex items-center justify-center p-6 text-gray-400 dark:text-gray-500">
                      <div className="text-center">
                        <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">{t('byproductMapping.noByproductsLinked')}</p>
                      </div>
                    </div>
                  ) : (
                    mappings.map((mapping) => (
                      <div
                        key={mapping.id}
                        className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {mapping.byproduct_name || mapping.byproduct_item_number}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {mapping.byproduct_item_number}
                            {mapping.byproduct_chinese_name &&
                              ` · ${mapping.byproduct_chinese_name}`}
                          </div>
                          {mapping.notes && (
                            <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 italic">
                              {mapping.notes}
                            </div>
                          )}
                        </div>
                        {confirmDeleteId === mapping.id ? (
                          <div className="flex items-center gap-2 ml-2">
                            <span className="text-xs text-red-600 dark:text-red-400">
                              {t('byproductMapping.confirmRemove')}
                            </span>
                            <button
                              onClick={() => deleteMutation.mutate(mapping.id)}
                              disabled={deleteMutation.isLoading}
                              className="px-2 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-colors"
                            >
                              {t('common.yes')}
                            </button>
                            <button
                              onClick={() => setConfirmDeleteId(null)}
                              className="px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded transition-colors"
                            >
                              {t('common.no')}
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setConfirmDeleteId(mapping.id)}
                            className="ml-2 p-1.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 rounded transition-colors"
                            title={t('byproductMapping.removeByproduct')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Existing Linkages overview - always visible */}
            <div>
              <div className={`p-3 border-b border-gray-200 dark:border-gray-700 ${selectedRawMaterial ? 'border-t-2 border-t-gray-300 dark:border-t-gray-600' : ''}`}>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('byproductMapping.existingLinkages')}
                </h3>
              </div>
              {loadingAllMappings ? (
                <div className="p-4 text-center text-sm text-gray-500">Loading...</div>
              ) : groupedMappings.length === 0 ? (
                <div className="flex items-center justify-center p-8 text-gray-400 dark:text-gray-500">
                  <div className="text-center">
                    <Link2 className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">{t('byproductMapping.noMappingsYet')}</p>
                  </div>
                </div>
              ) : (
                groupedMappings.map(([rawMaterialNumber, group]) => (
                  <div
                    key={rawMaterialNumber}
                    className={`border-b border-gray-200 dark:border-gray-700 ${selectedRawMaterial === rawMaterialNumber ? 'bg-primary-50/50 dark:bg-primary-900/20' : ''}`}
                  >
                    {/* Clickable raw material header - selects it in the left panel */}
                    <button
                      onClick={() => setSelectedRawMaterial(rawMaterialNumber)}
                      className="w-full text-left px-4 py-2.5 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                          {group.rawMaterialName || rawMaterialNumber}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {rawMaterialNumber}
                          {group.rawMaterialChineseName && ` · ${group.rawMaterialChineseName}`}
                          <span className="ml-2 text-primary-500">
                            {t('byproductMapping.byproductCount').replace('{{count}}', String(group.byproducts.length))}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-primary-500 flex-shrink-0 ml-2" />
                    </button>
                    {/* By-products listed under this raw material */}
                    <div className="pl-6 pr-4 pb-2">
                      {group.byproducts.map((bp) => (
                        <div key={bp.id} className="py-1.5 flex items-start gap-2">
                          <span className="text-gray-300 dark:text-gray-600 mt-0.5 text-xs">•</span>
                          <div className="min-w-0">
                            <div className="text-sm text-gray-700 dark:text-gray-300 truncate">
                              {bp.byproduct_name || bp.byproduct_item_number}
                            </div>
                            <div className="text-xs text-gray-400 dark:text-gray-500 truncate">
                              {bp.byproduct_item_number}
                              {bp.byproduct_chinese_name && ` · ${bp.byproduct_chinese_name}`}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add By-Product Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
            {/* Modal header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('byproductMapping.addByproduct')}
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal body */}
            <div className="p-4 space-y-4">
              {/* Search input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('byproductMapping.searchItems')}
                </label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={itemSearch}
                    onChange={(e) => {
                      setItemSearch(e.target.value);
                      setSelectedItem(null);
                    }}
                    placeholder={t('byproductMapping.searchItems')}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    autoFocus
                  />
                </div>
              </div>

              {/* Search results */}
              {itemSearch.length >= 2 && (
                <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-md">
                  {searching ? (
                    <div className="p-3 text-center text-sm text-gray-500">Loading...</div>
                  ) : filteredSearchResults.length === 0 ? (
                    <div className="p-3 text-center text-sm text-gray-500">
                      {searchResults.length > 0 && filteredSearchResults.length === 0
                        ? t('byproductMapping.alreadyLinked')
                        : t('byproductMapping.noResults')}
                    </div>
                  ) : (
                    filteredSearchResults.map((item) => (
                      <button
                        key={item.item_number}
                        onClick={() => setSelectedItem(item)}
                        className={`w-full text-left px-3 py-2 border-b border-gray-100 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                          selectedItem?.item_number === item.item_number
                            ? 'bg-primary-50 dark:bg-primary-900/30'
                            : ''
                        }`}
                      >
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {item.item_english_name || item.item_number}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {item.item_number}
                          {item.item_chinese_name && ` · ${item.item_chinese_name}`}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}

              {/* Selected item display */}
              {selectedItem && (
                <div className="p-2 bg-primary-50 dark:bg-primary-900/20 rounded-md border border-primary-200 dark:border-primary-800">
                  <div className="text-sm font-medium text-primary-700 dark:text-primary-300">
                    {selectedItem.item_english_name || selectedItem.item_number}
                  </div>
                  <div className="text-xs text-primary-600 dark:text-primary-400">
                    {selectedItem.item_number}
                  </div>
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('byproductMapping.notes')}
                </label>
                <input
                  type="text"
                  value={newNotes}
                  onChange={(e) => setNewNotes(e.target.value)}
                  placeholder={`${t('byproductMapping.notes')} (${t('common.optional')})`}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Modal footer */}
            <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-md transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCreate}
                disabled={!selectedItem || createMutation.isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors"
              >
                {createMutation.isLoading ? 'Saving...' : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ByproductMappingPage;
